import type { Directive, DirectiveBinding } from 'vue'

// Directiva global `v-in-view`: ejecuta `handler` cuando el elemento entra en el viewport (o en el de `root`),
// usando IntersectionObserver. Pensada para lazy load visual (cargar/renderizar más de a tandas a medida que un
// sentinel al final de una lista entra en pantalla), equivalente en Vue al directive `in-view` que ya se usa en
// Angular (ver resourcesPanel.component.js). También sirve para cualquier otro caso de "ejecutar algo cuando esto
// se ve" (lazy load de imágenes, tracking de impresiones, animaciones on-scroll, etc).
//
// Uso:
//   <div v-in-view="loadMore" />
//   <div v-in-view="{ handler: loadMore, rootMargin: '200px', once: false }" />

type InViewHandler = (entry: IntersectionObserverEntry) => void

interface InViewOptions {
  handler: InViewHandler
  root?: Element | null,     // contenedor scrolleable a observar; null (default) = viewport del documento
  rootMargin?: string,       // margen extra alrededor del root para disparar antes/después de que sea visible de verdad
  threshold?: number | number[],
  once?: boolean,            // deja de observar en cuanto entra en vista una vez (para animaciones/tracking puntuales)
}

type InViewValue = InViewHandler | InViewOptions

interface InViewElement extends HTMLElement {
  _inViewObserver?: IntersectionObserver
  _inViewOptions?: InViewOptions
}

// Normaliza el valor de la directiva (función corta o config completa) a un objeto de opciones
function resolveOptions(value: InViewValue): InViewOptions {
  return typeof value === 'function' ? { handler: value } : value
}

// True si cambió algo que obliga a recrear el IntersectionObserver (el handler y `once` se leen en cada disparo)
function observerConfigChanged(a: InViewOptions, b: InViewOptions): boolean {
  return a.root !== b.root || a.rootMargin !== b.rootMargin || a.threshold !== b.threshold
}

function mounted(el: InViewElement, binding: DirectiveBinding<InViewValue>) {
  const options = resolveOptions(binding.value)
  el._inViewOptions = options
  const observer = new IntersectionObserver((entries) => {
    const entry = entries[0]
    const current = el._inViewOptions
    if (!entry?.isIntersecting || !current) return
    current.handler(entry)
    if (current.once) unmounted(el)
  }, {
    root: options.root ?? null,
    rootMargin: options.rootMargin ?? '0px',
    threshold: options.threshold ?? 0,
  })
  el._inViewObserver = observer
  observer.observe(el)
}

// Un objeto literal inline en el template es una referencia nueva en cada render: reconectar el observer por eso
// lo haría disparar de nuevo si el elemento sigue visible (cascada de cargas). Solo se reconecta si cambió su config.
function updated(el: InViewElement, binding: DirectiveBinding<InViewValue>) {
  const options = resolveOptions(binding.value)
  const previous = el._inViewOptions
  if (!el._inViewObserver) return
  if (previous && !observerConfigChanged(previous, options)) {
    el._inViewOptions = options
    return
  }
  unmounted(el)
  mounted(el, binding)
}

function unmounted(el: InViewElement) {
  el._inViewObserver?.disconnect()
  el._inViewObserver = undefined
  el._inViewOptions = undefined
}

export const vInView: Directive<InViewElement, InViewValue> = { mounted, updated, unmounted }
export default vInView
